import { SettingPair } from "@nnr/settings";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  settingsControllerGetAllSettings,
  settingsControllerSetSetting,
} from "../lib/gen-api";
import { queryKeys } from "../lib/query-keys";

type SettingValue<T extends SettingPair["key"]> = Extract<
  SettingPair,
  { key: T }
>["value"];

export function useSettingsData(groupUuid?: string | null) {
  const queryClient = useQueryClient();

  const { data: settings, isPending } = useQuery({
    enabled: !!groupUuid,
    queryKey: queryKeys.settings(groupUuid!),
    queryFn: () => {
      return settingsControllerGetAllSettings({
        path: {
          groupUuid: groupUuid!,
        },
      }).then((r) => r.data?.data ?? []);
    },
  });

  const { mutateAsync: setValue } = useMutation({
    mutationKey: queryKeys.settings(groupUuid!),
    mutationFn: async (newSetting: SettingPair) => {
      await settingsControllerSetSetting({
        path: {
          groupUuid: groupUuid!,
        },
        body: newSetting,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.settings(groupUuid!),
      });
    },
  });

  return {
    settings: settings ?? [],
    setValue,
    isLoading: isPending,
  };
}

export function useSettingData<T extends SettingPair["key"]>(
  groupUuid: string | null | undefined,
  key: T,
) {
  const { settings, isLoading, setValue } = useSettingsData(groupUuid);

  if (isLoading) {
    return {
      value: null as SettingValue<T> | null,
      setValue: async (_newValue: SettingValue<T> | null) => {},
      isLoading: true as const,
    };
  }

  // If there is a default and a group specific one we choose the group specific one first.
  const special = settings.find(
    (s) => s.groupUuid === groupUuid && s.key === key,
  );

  if (special) {
    return {
      value: special.value as SettingValue<T>,
      setValue: async (newValue: SettingValue<T> | null) => {
        await setValue({ key: key, value: newValue } as SettingPair);
      },
      isLoading: false as const,
    };
  }

  // otherwise we fall back on the general.
  const fallbackValue = settings.find(
    (s) => s.groupUuid === null && s.key === key,
  )?.value;

  return {
    value: (fallbackValue as SettingValue<T> | undefined) ?? null,
    setValue: async (newValue: SettingValue<T> | null) => {
      await setValue({ key: key, value: newValue } as SettingPair);
    },
    isLoading: false as const,
  };
}
